
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import Actions from '../../../components/docs/Actions';
import Rocket from 'plaid-threads/Icons/Rocket';
export const metadata = {
  toc: true,
  maxTocLevel: 4,
  layout: 'guide',
  section: 'Transfer',
  title: 'Tracking Transfer status',
  description: 'Monitor for updates to transfers, sweeps, and refunds'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <Header title='Tracking transfer status' subtitle={metadata.description} mdxType="Header" />
    <h4 {...{
      "id": "transfer-status-lifecycle"
    }}>{`Transfer status lifecycle`}</h4>
    <p>{`All transfers initiated via ACH (including refunds and sweeps) move through a set of `}<inlineCode parentName="p">{`status`}</inlineCode>{`es that correspond to phases in the ACH lifecycle. Understanding these lets you forecast funds availability and surface accurate messaging to your end users.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Status`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Typical timing after /transfer/create`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`ACH Network Phase`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Business significance / recommended actions`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`pending`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Instant (<1s)`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Payment instruction received by Plaid and queued for processing`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Transfer can still be cancelled until it moves to posted. Safe to show "processing" to users.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`posted`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`At cutoff of applicable ACH window (e.g. 8:20 AM, 12:50 PM, 3:00 PM, or 8:30 PM ET)`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Payment instruction released to the ACH Network via ODFI`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Transfer has been submitted to the payment network and can no longer be cancelled.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`settled`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Same Day ACH: ~8:00 PM ET T; Standard ACH: ~9:00 AM ET T+1`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The Federal Reserve settles funds between sending and receiving banks`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`For debits: funds have been withdrawn from the debited account and settled to Plaid's FBO bank account. For credits: funds have been withdrawn from Plaid's FBO bank account and settled to the receiving institution.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`funds_available`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`2-5 business days after settlement`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`N/A`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`For debits only. Funds released from the Ledger's pending balance and applied to its available balance. Debits that do not return will remain in this status.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`failed`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Seconds to minutes after a failure`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Entry rejected before leaving Plaid/ODFI (e.g. compliance or validation failure)`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`No funds movement occurred. Investigate cause and optionally retry.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`cancelled`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Seconds after calling `}<inlineCode parentName="td">{`/transfer/cancel`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Client-initiated cancellation accepted`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The transfer will not be submitted to the payment network.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`returned`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Typically T+2 - T+5 (can be up to 60 days for consumer debits)`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Receiving FI returns the debit or credit with a return code`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`For ACH debits: funds are withdrawn from the Ledger's pending or available balance (depending on the timing of the return) to fund the reversal. For ACH credits: funds are returned to the Ledger's available balance. Take action per return code.`}</td>
        </tr>
      </tbody>
    </table>
    <h4 {...{
      "id": "event-monitoring"
    }}>{`Event monitoring`}</h4>
    <p>{`Plaid creates a transfer event any time the `}<inlineCode parentName="p">{`transfer.status`}</inlineCode>{` changes. For example, when a transfer is sent to the payment network, the `}<inlineCode parentName="p">{`transfer.status`}</inlineCode>{` moves to `}<inlineCode parentName="p">{`posted`}</inlineCode>{` and a `}<inlineCode parentName="p">{`posted`}</inlineCode>{` event is emitted. By monitoring transfer events, you can stay informed about their current status and notify customers in case of a canceled, failed, or returned transfer. When `}<inlineCode parentName="p">{`transfer.status`}</inlineCode>{` moves to `}<inlineCode parentName="p">{`settled`}</inlineCode>{`, you can expect that the consumer can see the transaction reflected in their personal bank account. Likewise, if a settled or posted transaction experiences an ACH return, a `}<inlineCode parentName="p">{`returned`}</inlineCode>{` event will be emitted as the `}<inlineCode parentName="p">{`transfer.status`}</inlineCode>{` is updated; for more details, see `}<a parentName="p" {...{
        "href": "/docs/transfer/troubleshooting/#ach-returns"
      }}>{`Troubleshooting ACH returns`}</a>{`.`}</p>
    <p>{`Events are also emitted for changes to a sweep's and refund's `}<inlineCode parentName="p">{`status`}</inlineCode>{` property. These events follow an `}<inlineCode parentName="p">{`[object].[status]`}</inlineCode>{` format, such as `}<inlineCode parentName="p">{`sweep.posted`}</inlineCode>{` and `}<inlineCode parentName="p">{`refund.posted`}</inlineCode>{`.`}</p>
    <p>{`For a list of all event types and descriptions, see the `}<a parentName="p" {...{
        "href": "/docs/api/products/transfer/reading-transfers/#transfer-event-sync-response-transfer-events-event-type"
      }}>{`API Reference`}</a>{`.`}</p>
    <h4 {...{
      "id": "ingesting-event-updates"
    }}>{`Ingesting event updates`}</h4>
    <p>{`Most integrations proactively monitor all events for every transfer. This allows you to respond to transfer events with business logic operations, such as:`}</p>
    <ul>
      <li parentName="ul">{`Kicking off the fulfillment of an order once the transfer has settled`}</li>
      <li parentName="ul">{`Making funds available to your end consumers for use in your application`}</li>
      <li parentName="ul">{`Monitoring returns to know when to claw these services back, or retry the transfer`}</li>
    </ul>
    <p>{`To do this, set up Transfer webhooks to listen for updates as they happen. You must register a URL to enable webhooks to be sent.`}</p>
    <p>{`You can do this in the `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/team/webhooks"
      }}>{`webhook settings page`}</a>{` of the Plaid Dashboard. Click `}<strong parentName="p">{`New Webhook`}</strong>{` and specify a webhook URL for a "Transfer event". You must be enabled for Production access to Transfer in order to access this option. To confirm that your endpoint has been correctly configured, you can trigger a test webhook via `}<inlineCode parentName="p">{`/sandbox/transfer/fire_webhook`}</inlineCode>{`. Only one webhook URL can be set per environment at a time; if you register multiple Transfer webhook receiver endpoints for a given environment, the webhooks will be sent to only one of the registered URLs.`}</p>
    <p>{`Now, every time there are new transfer events, Plaid will fire a notification webhook.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "#Example webhook payload",
        "#Example": true,
        "webhook": true,
        "payload": true
      }}>{`{
  "webhook_type": "TRANSFER",
  "webhook_code": "TRANSFER_EVENTS_UPDATE",
  "environment": "production"
}
`}</code></pre>
    <p>{`To receive details about the event, call `}<inlineCode parentName="p">{`/transfer/event/sync`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "#Example request payload",
        "#Example": true,
        "request": true,
        "payload": true
      }}>{`{
  "after_id": 4,
  "count": 20
}
`}</code></pre>
    <p>{`(`}<inlineCode parentName="p">{`after_id`}</inlineCode>{` is the highest `}<inlineCode parentName="p">{`event_id`}</inlineCode>{` from the previous response; this fetches the next 20 transfer events.)`}</p>
    <p>{`You can then store the highest `}<inlineCode parentName="p">{`event_id`}</inlineCode>{` returned by the response and use that value as the `}<inlineCode parentName="p">{`after_id`}</inlineCode>{` the next time you call `}<inlineCode parentName="p">{`/transfer/event/sync`}</inlineCode>{` to get only the new events.`}</p>
    <p>{`Note that webhooks don't contain any identifying information about what transfer has updated; only that an update happened. As an alternative to listening to webhooks, your application could also call `}<inlineCode parentName="p">{`/transfer/event/sync`}</inlineCode>{` on a regular basis to process the most recent batch of Transfer events.`}</p>
    <p>{`For a real-life example of an app that incorporates the transfer webhook and tests it using the `}<inlineCode parentName="p">{`/sandbox/transfer/fire_webhook`}</inlineCode>{` endpoint, see the Node-based `}<a parentName="p" {...{
        "href": "https://github.com/plaid/transfer-quickstart"
      }}>{`Transfer Quickstart`}</a>{` sample app. The Transfer Quickstart is a sample bill-pay app that enables ACH bank transfers. The Transfer webhook handler can be found in `}<a parentName="p" {...{
        "href": "https://github.com/plaid/transfer-quickstart/blob/main/server/webhookServer.js"
      }}>{`webhookServer.js`}</a>{` and the code which fires the webhook can be found at `}<a parentName="p" {...{
        "href": "https://github.com/plaid/transfer-quickstart/blob/main/server/routes/debug.js"
      }}>{`debug.js`}</a>{`.`}</p>
    <h4 {...{
      "id": "filtering-for-specific-events"
    }}>{`Filtering for specific events`}</h4>
    <p>{`Calling `}<inlineCode parentName="p">{`/transfer/event/list`}</inlineCode>{` will get a list of transfer events based on specified filter criteria. For example, you could search for all events for a specific `}<inlineCode parentName="p">{`transfer_id`}</inlineCode>{`. If you do not specify any filter criteria, this endpoint will return the latest 25 transfer events.`}</p>
    <p>{`You can apply filters to only fetch specific event types, events for a specific transfer type, a specific sweep, etc.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "#Example transfer event payload",
        "#Example": true,
        "transfer": true,
        "event": true,
        "payload": true
      }}>{`{
  "transfer_events": [
    {
      "account_id": "3gE5gnRzNyfXpBK5wEEKcymJ5albGVUqg77gr",
      "transfer_amount": "12.34",
      "transfer_id": "460cbe92-2dcc-8eae-5ad6-b37d0ec90fd9",
      "transfer_type": "credit",
      "event_id": 1,
      "event_type": "pending",
      "failure_reason": null,
      "funding_account_id": null,
      "originator_client_id": null,
      "refund_id": null,
      "sweep_amount": null,
      "sweep_id": null,
      "timestamp": "2019-12-09T17:27:15Z"
    }
  ],
  "request_id": "mdqfuVxeoza6mhu"
}
`}</code></pre>
    <h4 {...{
      "id": "reconciling-sweeps-with-your-bank-account"
    }}>{`Reconciling sweeps with your bank account`}</h4>
    <p>{`As Plaid moves money in and out of your business account as you process transfers and cash out the Plaid Ledger balance, you might want to match the account activity in your bank account with the associated transfers.`}</p>
    <p>{`Plaid will deposit or draw money from your business checking account in the form of a `}<a parentName="p" {...{
        "href": "/docs/transfer/creating-transfers/#sweeping-funds-to-funding-accounts"
      }}>{`sweep`}</a>{`. This means that any time you are interacting with your bank statement, you are viewing sweeps, not specific transfers.`}</p>
    <p>{`To match an entry in your bank account with a sweep in Plaid's records, Plaid ensures the first 8 characters of the sweep's `}<inlineCode parentName="p">{`sweep_id`}</inlineCode>{` will show up on your bank statements. For example, consider the following entries in your bank account from Plaid:`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Entry`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Amount`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Date`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`PLAID 6c036ea0 CCD`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`-$5,264.62`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`November 18, 2022`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`PLAID ae42c210 CCD`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`$2,367.80`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`November 16, 2022`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`PLAID 550c85fc CCD`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`$6,007.49`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`November 10, 2022`}</td>
        </tr>
      </tbody>
    </table>
    <p>{`You can use this 8 character string from your bank statement to search for the sweep via `}<inlineCode parentName="p">{`/transfer/sweep/get`}</inlineCode>{`, or in your Plaid Transfer dashboard. `}</p>
    <p>{`To follow the lifecycle of a sweep, and monitor funds coming into and out of your business checking account due to Plaid Transfer activity, observe the `}<inlineCode parentName="p">{`sweep.*`}</inlineCode>{` events in the `}<inlineCode parentName="p">{`/transfer/event/sync`}</inlineCode>{` endpoint. To view all sweeps, use `}<inlineCode parentName="p">{`/transfer/sweep/list`}</inlineCode>{`.`}</p>
    <p>{`To see if a given transfer has been included in a sweep, check the transfer's `}<inlineCode parentName="p">{`sweep_status`}</inlineCode>{` field. `}<inlineCode parentName="p">{`swept`}</inlineCode>{` means the transfer was included in a sweep to the sweep account; `}<inlineCode parentName="p">{`swept_settled`}</inlineCode>{` means credits are available to be withdrawn or debits have been deducted from your business checking account; `}<inlineCode parentName="p">{`return_swept`}</inlineCode>{` means the transfer was returned and funds were pulled or pushed back. `}</p>
    <h4 {...{
      "id": "performing-financial-reconciliation-audits"
    }}>{`Performing financial reconciliation audits`}</h4>
    <p>{`For information on performing financial reconciliation audits, see `}<a parentName="p" {...{
        "href": "/docs/transfer/dashboard/#report-extraction"
      }}>{`Report extraction`}</a>{`.`}</p>
    <Actions.Wrapper>
  <Actions.Card icon={<Rocket mdxType="Rocket" />} title='Flow of funds' subtitle='Understand how your money moves through the Plaid network' link='/docs/transfer/flow-of-funds' linkText='Learn more' />
    </Actions.Wrapper>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "transfer-status-lifecycle",
        level: 4,
        title: "Transfer status lifecycle",
      },
{
        id: "event-monitoring",
        level: 4,
        title: "Event monitoring",
      },
{
        id: "ingesting-event-updates",
        level: 4,
        title: "Ingesting event updates",
      },
{
        id: "filtering-for-specific-events",
        level: 4,
        title: "Filtering for specific events",
      },
{
        id: "reconciling-sweeps-with-your-bank-account",
        level: 4,
        title: "Reconciling sweeps with your bank account",
      },
{
        id: "performing-financial-reconciliation-audits",
        level: 4,
        title: "Performing financial reconciliation audits",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
